import React from 'react';
import {
  ArrowRight,
  ArrowLeft,
  Globe,
  Sparkles,
  LayoutTemplate,
  ShoppingBag,
  Cpu,
  Layers,
  Rocket,
  CheckCircle2,
} from 'lucide-react';
import PublicHeader from './PublicHeader';

const FashionWebsiteInfoPage = ({ onBack, onGetStarted, isAuthenticated, setActiveView, onSupportClick }) => {
  const pillars = [
    {
      icon: LayoutTemplate,
      title: 'Fashion-native templates',
      desc: 'Layouts built for lookbooks, drops, and size stories—not generic SaaS blocks you fight to restyle.',
    },
    {
      icon: Layers,
      title: 'Catalogue in sync',
      desc: 'Connect your Fashdeck fabrics and collections so the site stays current when you publish updates.',
    },
    {
      icon: Cpu,
      title: 'Generated concepts on the page',
      desc: 'Drop in AI-generated looks, virtual try-on moments, and studio renders as first-class sections customers can explore.',
    },
    {
      icon: ShoppingBag,
      title: 'Ready for commerce',
      desc: 'Product pages, variants, and checkout paths designed for apparel—without rebuilding your brand from scratch.',
    },
  ];

  const phases = [
    {
      phase: 'Phase 1 — Site shell',
      items: [
        'Pick a fashion-focused template, colours, and typography tied to your brand.',
        'Connect a custom domain and basic pages: home, story, contact, policies.',
      ],
    },
    {
      phase: 'Phase 2 — Merchandising',
      items: [
        'Import or sync products from Fashdeck catalogues and tag collections.',
        'Configure sizing, care content, and rich imagery including AI and flat lays.',
      ],
    },
    {
      phase: 'Phase 3 — AI storefront',
      items: [
        'Embed generated concepts: curated galleries, “shop the look” from generated outfits, and optional VTO entry points.',
        'Credit-aware usage so teams control which AI assets are public on the site.',
      ],
    },
    {
      phase: 'Phase 4 — Sell & scale',
      items: [
        'Cart, payments, and order notifications with fashion-friendly fulfilment hooks.',
        'SEO, analytics, and growth loops (waitlists, new-drop alerts) built in.',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-violet-50 to-slate-50 text-slate-900">
      <PublicHeader
        activeViewId="fashion-website-info"
        setActiveView={setActiveView}
        onSupportClick={onSupportClick}
      />
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 pt-24 sm:pt-28 pb-16">
        <div className="flex items-center justify-between gap-3 mb-10">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900 rounded-xl px-3 py-2 hover:bg-white/70 border border-slate-200 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3 min-w-0">
            <div className="fd-brand-mark w-10 h-10 rounded-xl shrink-0">
              <img src="/logo.png?v=20260711" alt="Fashdeck" />
            </div>
            <div className="leading-tight min-w-0">
              <p className="text-sm font-bold text-slate-900 max-w-[10rem] sm:max-w-none leading-tight">
                Build your fashion website
              </p>
              <p className="text-xs text-slate-500">Ecommerce + AI, one roadmap</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div className="relative lg:sticky lg:top-24 order-1 lg:order-2">
            <div className="absolute -inset-6 bg-gradient-to-r from-violet-600/15 to-fuchsia-600/15 blur-3xl" />
            <div className="relative bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden ring-1 ring-black/5">
              <div className="aspect-[4/3] bg-gradient-to-br from-slate-900 via-violet-900 to-fuchsia-900 flex flex-col items-center justify-center p-8 text-center">
                <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center ring-1 ring-white/20 mb-4">
                  <Globe className="w-9 h-9 text-white" aria-hidden />
                </div>
                <p className="text-white font-bold text-lg sm:text-xl">Your brand. Your domain.</p>
                <p className="text-violet-100 text-sm mt-2 max-w-xs">
                  A planned storefront layer on top of Fashdeck—catalogues, generated concepts, and checkout in one place.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white ring-1 ring-white/20">
                    <Sparkles className="w-3.5 h-3.5" />
                    AI sections
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white ring-1 ring-white/20">
                    <ShoppingBag className="w-3.5 h-3.5" />
                    Commerce
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white ring-1 ring-white/20">
                    <Layers className="w-3.5 h-3.5" />
                    Catalogue sync
                  </span>
                </div>
              </div>
              <div className="p-6 sm:p-8 border-t border-slate-200 bg-white">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Rocket className="w-4 h-4 text-violet-600 shrink-0" />
                  Product plan (high level)
                </div>
                <ol className="mt-4 space-y-4">
                  {phases.map(({ phase, items }) => (
                    <li key={phase}>
                      <p className="text-xs font-bold uppercase tracking-wide text-violet-700">{phase}</p>
                      <ul className="mt-2 space-y-2">
                        {items.map((item) => (
                          <li key={item} className="flex gap-2 text-sm text-slate-700">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>

          <div className="space-y-6 order-2 lg:order-1">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
              Build an ecommerce fashion website on Fashdeck
            </h1>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              We are shaping a path from your existing Fashdeck catalogues and AI Studio output to a customer-facing site:
              on-brand pages, shoppable products, and embedded generated concepts so visitors see your tech and your textiles
              together.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {pillars.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="bg-white/80 backdrop-blur rounded-2xl border border-slate-200 p-4 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-sm ring-1 ring-black/5 shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-slate-900">{title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-violet-200 bg-violet-50/80 p-5 text-sm text-slate-700">
              <p className="font-semibold text-slate-900">Adding generated concepts to the site</p>
              <p className="mt-2 leading-relaxed">
                Authors will choose which generated concepts (and optional virtual try-on flows) appear on the homepage,
                collection pages, or product detail—reusing assets you already generate in Fashdeck, with clear
                publishing controls so only approved looks go live.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <button
                type="button"
                onClick={onGetStarted}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-semibold shadow-sm hover:bg-slate-800 transition-all group"
              >
                {isAuthenticated ? 'Open AI Studio' : 'Log in to explore'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-800 font-semibold hover:bg-white transition-all"
              >
                Explore Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FashionWebsiteInfoPage;
